import React from "react";

/**
 * Subtle decorative motif rendered in a corner of each service card.
 * Every service gets its own micro-diagram so cards never look identical.
 * All monochrome, non-informational, purely visual identity.
 */
export default function ServiceDecor({ slug }) {
  const D = DECOR[slug] || DECOR.default;
  return (
    <svg
      viewBox="0 0 120 120"
      className="w-24 h-24 md:w-32 md:h-32 opacity-90"
      aria-hidden
    >
      <defs>
        <linearGradient id={`sd-${slug}`} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#0a0a0a" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#0a0a0a" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      {D(slug)}
    </svg>
  );
}

const stroke = { stroke: "#0a0a0a", strokeOpacity: 0.5, fill: "none", strokeWidth: 1 };
const dot = { fill: "#0a0a0a" };

const DECOR = {
  // 01 Product Sourcing — radar / target
  "product-sourcing": (id) => (
    <g>
      {[18, 32, 46, 60].map((r) => (
        <circle key={r} cx="60" cy="60" r={r} {...stroke} strokeDasharray={r === 18 ? "0" : "2 4"} />
      ))}
      <line x1="10" y1="60" x2="110" y2="60" {...stroke} strokeOpacity="0.15" />
      <line x1="60" y1="10" x2="60" y2="110" {...stroke} strokeOpacity="0.15" />
      <circle cx="60" cy="60" r="3" {...dot} />
      <circle cx="86" cy="46" r="2.5" {...dot} />
    </g>
  ),

  // 02 Supplier Sourcing — scanning sweep
  "supplier-sourcing": () => (
    <g>
      <circle cx="60" cy="60" r="52" {...stroke} strokeDasharray="2 6" />
      <path d="M 60 60 L 60 8 A 52 52 0 0 1 108 68 Z" fill={`url(#sd-supplier-sourcing)`} opacity="0.35" />
      <circle cx="60" cy="60" r="3" {...dot} />
      <circle cx="90" cy="28" r="2" {...dot} />
      <circle cx="30" cy="80" r="2" {...dot} />
      <circle cx="85" cy="85" r="2" {...dot} />
    </g>
  ),

  // 03 Supplier Verification — checkmark inside shield
  "supplier-verification": () => (
    <g>
      <path
        d="M 60 12 L 100 24 L 100 60 C 100 88, 82 104, 60 110 C 38 104, 20 88, 20 60 L 20 24 Z"
        {...stroke}
      />
      <path d="M 40 62 L 55 76 L 82 48" {...stroke} strokeWidth="1.5" />
    </g>
  ),

  // 04 Manufacturing — interlocking gears (outline only)
  "manufacturing-services": () => (
    <g>
      {[
        [42, 52, 22],
        [78, 68, 18],
      ].map(([cx, cy, r], i) => (
        <g key={i}>
          <circle cx={cx} cy={cy} r={r} {...stroke} />
          <circle cx={cx} cy={cy} r={r - 8} {...stroke} />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
            const rad = (a * Math.PI) / 180;
            const x1 = cx + Math.cos(rad) * r;
            const y1 = cy + Math.sin(rad) * r;
            const x2 = cx + Math.cos(rad) * (r + 5);
            const y2 = cy + Math.sin(rad) * (r + 5);
            return <line key={a} x1={x1} y1={y1} x2={x2} y2={y2} {...stroke} />;
          })}
        </g>
      ))}
    </g>
  ),

  // 05 Production Management — line chart / milestones
  "production-management": () => (
    <g>
      {[20, 40, 60, 80, 100].map((x) => (
        <line key={x} x1={x} y1="20" x2={x} y2="100" {...stroke} strokeOpacity="0.1" />
      ))}
      <polyline
        points="10,90 30,72 50,80 70,50 90,60 110,30"
        {...stroke}
        strokeWidth="1.5"
      />
      {[
        [30, 72],
        [70, 50],
        [110, 30],
      ].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="3" {...dot} />
      ))}
    </g>
  ),

  // 06 Quality Control — AQL sampling grid with markers
  "quality-control": () => (
    <g>
      {[0, 1, 2, 3, 4, 5, 6].map((r) =>
        [0, 1, 2, 3, 4, 5, 6].map((c) => (
          <circle
            key={`${r}-${c}`}
            cx={20 + c * 13}
            cy={20 + r * 13}
            r="1.5"
            {...dot}
            fillOpacity={
              (r === 2 && c === 3) || (r === 4 && c === 1) || (r === 5 && c === 5)
                ? 1
                : 0.2
            }
          />
        ))
      )}
      <rect x="15" y="15" width="90" height="90" {...stroke} />
    </g>
  ),

  // 07 Factory Inspection — clipboard/document
  "factory-inspection": () => (
    <g>
      <rect x="30" y="20" width="60" height="80" rx="4" {...stroke} />
      <rect x="46" y="12" width="28" height="14" rx="3" {...stroke} />
      <line x1="40" y1="42" x2="80" y2="42" {...stroke} strokeOpacity="0.4" />
      <line x1="40" y1="54" x2="80" y2="54" {...stroke} strokeOpacity="0.4" />
      <line x1="40" y1="66" x2="70" y2="66" {...stroke} strokeOpacity="0.4" />
      <line x1="40" y1="78" x2="80" y2="78" {...stroke} strokeOpacity="0.4" />
      <path d="M 42 90 L 50 96 L 66 82" {...stroke} strokeWidth="1.5" />
    </g>
  ),

  // 08 Trading — bidirectional arrows
  "trading-services": () => (
    <g>
      <line x1="18" y1="46" x2="102" y2="46" {...stroke} strokeWidth="1.25" />
      <polyline points="94,38 102,46 94,54" {...stroke} strokeWidth="1.25" />
      <line x1="102" y1="74" x2="18" y2="74" {...stroke} strokeWidth="1.25" />
      <polyline points="26,66 18,74 26,82" {...stroke} strokeWidth="1.25" />
      <circle cx="14" cy="46" r="4" {...stroke} />
      <circle cx="106" cy="46" r="4" {...stroke} />
      <circle cx="14" cy="74" r="4" {...stroke} />
      <circle cx="106" cy="74" r="4" {...stroke} />
    </g>
  ),

  // 09 Import & Export — globe meridians
  "import-export-support": () => (
    <g>
      <circle cx="60" cy="60" r="46" {...stroke} />
      <ellipse cx="60" cy="60" rx="46" ry="18" {...stroke} strokeOpacity="0.4" />
      <ellipse cx="60" cy="60" rx="20" ry="46" {...stroke} strokeOpacity="0.4" />
      <line x1="14" y1="60" x2="106" y2="60" {...stroke} strokeOpacity="0.3" />
      <line x1="60" y1="14" x2="60" y2="106" {...stroke} strokeOpacity="0.3" />
      <circle cx="76" cy="40" r="2" {...dot} />
      <circle cx="42" cy="72" r="2" {...dot} />
    </g>
  ),

  // 10 Business Representation — orbiting satellites
  "business-representation": () => (
    <g>
      <circle cx="60" cy="60" r="8" {...dot} />
      <ellipse cx="60" cy="60" rx="48" ry="20" {...stroke} strokeDasharray="2 4" />
      <ellipse
        cx="60"
        cy="60"
        rx="20"
        ry="48"
        {...stroke}
        strokeDasharray="2 4"
        transform="rotate(30 60 60)"
      />
      <circle cx="108" cy="60" r="3" {...dot} />
      <circle cx="60" cy="12" r="3" {...dot} />
      <circle cx="26" cy="86" r="3" {...dot} />
    </g>
  ),

  default: () => <circle cx="60" cy="60" r="40" {...stroke} />,
};
